import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client/react";
import { ArrowLeft, Loader2, Sparkles } from "lucide-react";

import { GENERATE_PROGRESS_REPORT } from "@/graphql/mutations/reports";
import { GET_STUDENT_PROGRESS_REPORTS } from "@/graphql/queries/reports";
import { ProgressReportCard } from "@/components/progress-report-view";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatFullDate, formatRelativeDate } from "@/lib/relative-date";

/**
 * Progress reports for one learner, for the grown-up raising them.
 *
 * One kind of report, not two on tabs. The page used to offer „Mokytojui" and
 * „Mokiniui" side by side, which was wrong twice over: there is no teacher here —
 * the reader is a parent, and the report is about their child, not about a pupil in
 * a class — and the child's letter is not a grown-up's deliverable. It arrives for
 * them, on their own profile, as an occasion; commissioning it from a tab in the
 * admin turns their letter into somebody else's errand. A guardian who wants to
 * hand something to their child hands them the letter they already have.
 */
export default function StudentProgressReportsPage() {
  const { id } = useParams<{ id: string }>();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data, loading, refetch } = useQuery(GET_STUDENT_PROGRESS_REPORTS, {
    variables: { studentId: id },
    skip: !id,
    fetchPolicy: "cache-and-network",
  });

  const [generate] = useMutation(GENERATE_PROGRESS_REPORT);

  const student = (data as any)?.student;
  const reports = (data as any)?.guardianReports ?? [];
  const availability = (data as any)?.guardianAvailability;
  const allowed = availability?.allowed === true;

  async function onGenerate() {
    setError(null);
    setBusy(true);
    try {
      const result = await generate({ variables: { studentId: id, audience: "guardian" } });
      const payload = (result.data as any)?.generateProgressReport;
      if (payload?.errors?.length) setError(payload.errors[0]);
      await refetch();
    } catch {
      setError("Nepavyko parengti ataskaitos");
    } finally {
      setBusy(false);
    }
  }

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/students/${id}`} aria-label="Atgal">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Pažangos ataskaitos</h1>
          <p className="text-sm text-muted-foreground">
            {student?.name}
            {student?.grade ? ` · ${student.grade} klasė` : ""}
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Nauja ataskaita</CardTitle>
          <CardDescription>
            Ką {student?.name || "vaikas"} išmoko, kur stringa ir ką daryti toliau — su įrodymais iš
            pačių sesijų ir pokalbių. Palyginama su praėjusia ataskaita. Vaikas jos nemato.
            {/*
              Reports now arrive on their own, and a grown-up who does not know that
              reads a list they did not ask for as something going wrong. The button
              stays: it is what you press when you want one *now*.
            */}{" "}
            Ataskaita parengiama automatiškai po kiekvienos baigtos sesijos — žemiau esantis
            mygtukas skirtas tada, kai norite jos iškart.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="text-muted-foreground">
              Naujų sesijų nuo praėjusios ataskaitos:{" "}
              <span className="font-semibold text-foreground">{availability?.sessionsDone ?? 0}</span>
            </span>
            {availability?.lastReportAt && (
              <span className="text-muted-foreground">
                · praėjusi {formatRelativeDate(availability.lastReportAt)}
              </span>
            )}
          </div>

          {/*
            The window is not the same thing as the new sessions above it, and a
            grown-up reading "1 new session" would otherwise expect a report about
            one session. It always reaches back over the last few, so there is
            something to compare against.
          */}
          <p className="text-xs text-muted-foreground">
            Ataskaita visada apžvelgia bent 5 paskutines sesijas (daugiausiai 20) — net tas, kurios
            jau buvo aprašytos. Kitaip nėra su kuo lyginti.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={onGenerate} disabled={!allowed || busy}>
              {busy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Rašoma...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Parengti
                </>
              )}
            </Button>
            {!allowed && availability?.reason && (
              <span className="text-sm text-muted-foreground">{availability.reason}</span>
            )}
          </div>
        </CardContent>
      </Card>

      {reports.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Dar nėra nė vienos ataskaitos.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reports.map((report: any, i: number) => (
            <ProgressReportCard
              key={report.id}
              report={report}
              title={i === 0 ? "Naujausia" : formatFullDate(report.createdAt)}
            >
              <div className="text-right text-xs text-muted-foreground">
                <div>{formatFullDate(report.createdAt)}</div>
                {/*
                  „system" is the app writing one after a session, and printing the
                  word back would read as somebody's name under a report nobody
                  pressed anything to get.
                */}
                {report.requestedByRole && (
                  <div>
                    {report.requestedByRole === "system"
                      ? "parengta automatiškai"
                      : `parengė: ${report.requestedByRole}`}
                  </div>
                )}
              </div>
            </ProgressReportCard>
          ))}
        </div>
      )}
    </div>
  );
}
